import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  ReturnTotalCalculations as Calculations,
  DraftReturn,
  ShipmentMethodName,
} from "@redotech/redo-model/draft-return/draft-return";
import { ProvisionType } from "@redotech/redo-model/return-flow";
import { SubtotalCollapseSection } from "@redotech/redo-web/card";
import { CurrencyContext } from "@redotech/redo-web/currency";
import Logo from "@redotech/redo-web/icon-old/logo-icon.svg";
import { Elements } from "@stripe/react-stripe-js";
import type { Stripe, StripeElements } from "@stripe/stripe-js";
import { memo, useContext } from "react";
import * as review from "../review.module.css";
import { InstantExchangeDisclaimer } from "./instant-exchange-disclaimer";
import { Payment } from "./payment";

export const LabelPaymentCard = memo(function LabelPaymentCard({
  settings,
  shipmentMethod,
  draftReturn,
  stripe,
  skipPayment,
  setElements,
  setPaymentInfoFilled,
  newOrderMethod,
  submitReturn,
  calculations,
}: {
  settings: ReturnAppSettings;
  shipmentMethod: ShipmentMethodName;
  draftReturn: DraftReturn;
  stripe: Stripe;
  skipPayment: boolean;
  setElements: (elements: StripeElements) => void;
  setPaymentInfoFilled: (filled: boolean) => void;
  newOrderMethod: ProvisionType;
  submitReturn: () => void;
  calculations: Calculations;
}) {
  const {
    newOrderValue,
    totalReturnValue,
    totalShippingFee,
    shippingFee,
    pickupFee,
  } = calculations;
  const { formatCurrencyFromUSD, formatCurrency } = useContext(CurrencyContext);
  return (
    <div className={review.summaryCard}>
      <div className={review.summaryHeader}>Return shipping label</div>
      {newOrderMethod === ProvisionType.INSTANT && (
        <InstantExchangeDisclaimer
          exchangeAmount={Math.min(newOrderValue, totalReturnValue)}
          settings={settings}
        />
      )}

      {shippingFee === 0 ? (
        <>
          <div className={review.summaryCardItem}>
            {!settings.hidePortalBranding && false && (
              <Logo className={review.logo} />
            )}
            <div className={review.summaryText}>Shipping fee</div>
            <div className={review.summaryText}>
              Free
              {shipmentMethod === ShipmentMethodName.IN_STORE &&
                " (returning In-Store)"}
            </div>
          </div>

          {draftReturn?.shipmentMethod?.method === ShipmentMethodName.PICKUP &&
            pickupFee && (
              <div className={review.summaryCardItem}>
                <div className={review.summaryText}>Package Pickup</div>
                <div className={review.summaryText}>
                  {formatCurrencyFromUSD(pickupFee)}
                </div>
              </div>
            )}
        </>
      ) : (
        <>
          {draftReturn?.shipmentMethod?.method !== ShipmentMethodName.PICKUP ? (
            <div className={review.summaryCardItem}>
              <div className={review.summaryText}>Shipping fee</div>
              {formatCurrency(shippingFee)}
            </div>
          ) : (
            <SubtotalCollapseSection
              header={{
                title: "Total Shipping Fee",
                total: formatCurrency(totalShippingFee),
              }}
              subtitleTextClassName={review.summaryTextLight}
              subtotals={[
                { title: "Shipping Fee", total: formatCurrency(shippingFee) },
                {
                  title: "Package Pickup",
                  total: formatCurrencyFromUSD(pickupFee!),
                },
              ]}
              titleTextClassName={review.summaryText}
            />
          )}
        </>
      )}
      {/* We will save the card for an instant exchange as a recovery charge */}
      {!skipPayment && (
        <div>
          <Elements stripe={stripe}>
            <Payment
              nextStep={submitReturn}
              setElements={setElements}
              setPaymentInfoFilled={setPaymentInfoFilled}
              settings={settings}
            />
          </Elements>
        </div>
      )}
    </div>
  );
});
